import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldIcon } from "@emerald-react/icon";
import { useSortableItem, useSortableList } from "@shared/hooks/useSortable";
import { ReactNode } from "react";
import { Accordion, AccordionGroup } from "./Accordion";
import {
  DragHandleProps,
  SortableAccordionData,
  SortableAccordionGroupProps,
  SortableAccordionItemProps,
  SortableAccordionItemWrapperProps,
} from "./types";

// Shared drag handle components
const DragHandle = ({ attributes, listeners }: DragHandleProps) => (
  <div
    className="drag-handle cursor-grab active:cursor-grabbing"
    {...attributes}
    {...listeners}
  >
    <EmeraldIcon
      icon="drag_indicator"
      className="text-gray-400 hover:text-gray-600 transition-colors"
    />
  </div>
);

const DragHandlePlaceholder = () => (
  <div className="drag-handle-placeholder w-6"></div>
);

// Wrapper component that conditionally uses sortable behavior
const SortableAccordionItemWrapper = ({
  item,
}: SortableAccordionItemWrapperProps) => {
  if (item.showDragHandle === false) {
    // Non-sortable item - render directly without dnd-kit
    return (
      <Accordion
        id={item.id}
        title={item.title}
        leftElement={<DragHandlePlaceholder />}
        rightElement={item.rightElement}
        disabled={item.disabled}
        defaultOpen={item.defaultOpen}
        titleNumber={item.titleNumber as ReactNode}
        titleEditable={item.titleEditable}
        titleEditValue={item.titleEditValue}
        titleInputClass={item.titleInputClass}
        onTitleChange={item.onTitleChange}
        onTitleKeyDown={item.onTitleKeyDown}
        onTitleBlur={item.onTitleBlur}
        onToggle={item.onToggle}
        titleError={item.titleError}
      >
        {item.children}
      </Accordion>
    );
  }

  // Sortable item - use hooks
  return <SortableAccordionItem item={item} />;
};

// Sortable item component (always uses hooks)
const SortableAccordionItem = ({ item }: SortableAccordionItemProps) => {
  const { attributes, listeners, setNodeRef, style } = useSortableItem(item.id);

  return (
    <div ref={setNodeRef} style={style}>
      <Accordion
        id={item.id}
        title={item.title}
        leftElement={
          <DragHandle attributes={attributes} listeners={listeners} />
        }
        rightElement={item.rightElement}
        disabled={item.disabled}
        defaultOpen={item.defaultOpen}
        titleNumber={item.titleNumber as ReactNode}
        titleEditable={item.titleEditable}
        titleEditValue={item.titleEditValue}
        titleInputClass={item.titleInputClass}
        onTitleChange={item.onTitleChange}
        onTitleKeyDown={item.onTitleKeyDown}
        onTitleBlur={item.onTitleBlur}
        onToggle={item.onToggle}
        titleError={item.titleError}
      >
        {item.children}
      </Accordion>
    </div>
  );
};

// Sortable accordion group component
export const SortableAccordionGroup = ({
  items,
  onReorder,
  onReorderByIndex,
  className = "",
  allowMultiple = false,
}: SortableAccordionGroupProps) => {
  // Filter only sortable items for dnd-kit context
  const sortableItems = items.filter((item) => item.showDragHandle !== false);

  // Use our reusable useSortableList hook instead of manual dnd-kit setup
  const { DndContextProps, SortableContextProps } = useSortableList({
    items: sortableItems, // Only pass sortable items
    onReorder: (
      reorderedItems: SortableAccordionData[],
      sourceIndex: number,
      destinationIndex: number,
    ) => {
      // We need to reconstruct the full array with non-sortable items in their original positions
      let fullReorderedItems = [...items];
      let sortableIndex = 0;

      for (let i = 0; i < fullReorderedItems.length; i++) {
        if (fullReorderedItems[i].showDragHandle !== false) {
          fullReorderedItems[i] = reorderedItems[sortableIndex];
          sortableIndex++;
        }
      }

      // Call index-based callback if provided (adjust indices for full array)
      if (onReorderByIndex) {
        // Calculate actual indices in the full array
        let actualSourceIndex = 0;
        let actualDestinationIndex = 0;
        let sortableCount = 0;

        for (let i = 0; i < items.length; i++) {
          if (items[i].showDragHandle !== false) {
            if (sortableCount === sourceIndex) actualSourceIndex = i;
            if (sortableCount === destinationIndex) actualDestinationIndex = i;
            sortableCount++;
          }
        }

        onReorderByIndex(actualSourceIndex, actualDestinationIndex);
      }

      // Call full array callback if provided
      if (onReorder) {
        onReorder(fullReorderedItems);
      }
    },
  });

  return (
    <DndContext {...DndContextProps}>
      <AccordionGroup className={className} allowMultiple={allowMultiple}>
        <SortableContext {...SortableContextProps}>
          {items.map((item) => (
            <SortableAccordionItemWrapper key={item.id} item={item} />
          ))}
        </SortableContext>
      </AccordionGroup>
    </DndContext>
  );
};

export default SortableAccordionGroup;
